import { useAtomValue } from 'jotai';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Workspace } from '@/features/workspace';
import { SourceInput } from '@/components/source-input';
import { SuggestionsNavigation } from '@/components/suggestion-navigation';
import { DocumentHeader } from '@/components/document-header';
import { EditorWorkspace } from '@/features/editor-workspace';
import { CortexProcessing } from '@/components/cortex-processing';
import { getUserSourceErrorMessage } from '@/components/context-item';

import { usePageTitle } from '@/hooks/use-page-title';
import { useDocumentId } from '@/hooks/use-document-id';
import { useStatusFavicon } from '@/hooks/use-status-favicon';
import { useNote, useNoteLazy, useNoteWriter, useUserSource, useUserSourceWriter } from '@/hooks/rxdb-models';
import type { MutationOptions } from '@/hooks/rxdb-base';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { useResyncUserSource } from '@/hooks/use-resync-user-source';
import {
  useReviewActions,
  toPlainPendingReview,
  type PendingReview,
  type PersistedDocumentWriter,
} from '@/hooks/llm/use-llm-agent';
import { type MenuOptionKey, useDocuMenu } from '@/hooks/use-docu-menu/use-docu-menu';

import { getDocumentIdFromPathname } from '@/utils/helper-utils';
import { appRoutes } from '@/constants/routes';
import { appReadyAtom, editorAtom } from '@/state';
import { useSyncStatus } from '@/providers/rxdb/sync-status';

import { NoteVariant } from '@/providers/rxdb/constants';
import type { UserSource } from '@flintmint/core-shared/rxdb';
import type { OriginNoteLink } from '@/hooks/use-item-actions';
import { CopyToClipboardBtn } from '@/components/copty-to-clipboard-btn';

const menuOptions: MenuOptionKey[] = ['duplicate', 'delete'];

type CortexWorkspaceLocationState = {
  originNoteLink?: OriginNoteLink;
};

export const CortexWorkspace = () => {
  const [documentTitle, setDocumentTitle] = useState('');
  const [showSuccessFavicon, setShowSuccessFavicon] = useState(false);

  const editor = useAtomValue(editorAtom);
  const appReady = useAtomValue(appReadyAtom);
  const { initialSyncComplete } = useSyncStatus();

  const prevSyncStatusRef = useRef<UserSource['syncStatus']>(undefined);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const hasAttachedOriginNoteRef = useRef(false);

  const location = useLocation();
  const navigateWithSearch = useNavigateWithSearch();

  const { id, isDraft } = useDocumentId();
  const originNoteLink = (location.state as CortexWorkspaceLocationState | null)?.originNoteLink;

  const { data: userSource, isLoading } = useUserSource(id);
  const { isResyncing } = useResyncUserSource(userSource);
  const { mutate: upsertUserSource } = useUserSourceWriter(userSource, id, { createIfMissing: isDraft });
  const documentPendingReview = (userSource?.pendingReview ?? null) as PendingReview | null;
  const persistUserSourceDocumentPatch: PersistedDocumentWriter = async ({ content, pendingReview }) => {
    await upsertUserSource({
      ...(content !== undefined && { content }),
      ...(pendingReview !== undefined && { pendingReview }),
    });
  };
  const { data: originNote } = useNote(originNoteLink?.id);
  const { mutate: upsertOriginNote } = useNoteWriter(originNote, originNoteLink?.id, {
    variant: (originNote?.variant ?? originNoteLink?.variant ?? 'notes') as NoteVariant,
    createIfMissing: !!originNoteLink?.isDraft,
  });
  const { query: queryNote } = useNoteLazy();

  const pendingReview = useMemo(
    () => (documentPendingReview ? toPlainPendingReview(documentPendingReview) : null),
    [documentPendingReview]
  );
  const review = useReviewActions({
    requestId: id!,
    editor,
    pendingReview,
    persistDocumentPatch: persistUserSourceDocumentPatch,
  });

  usePageTitle(documentTitle);

  const getMenuData = useDocuMenu(menuOptions);

  const processingFailed = userSource?.syncStatus === 'error';
  const showProcessing = isResyncing || processingFailed;
  const effectiveSyncStatus: UserSource['syncStatus'] | undefined = isResyncing ? 'processing' : userSource?.syncStatus;

  useStatusFavicon(getFaviconStatus({ isResyncing, processingFailed, showSuccessFavicon }));

  useEffect(() => {
    const current = effectiveSyncStatus;
    const prev = prevSyncStatusRef.current;

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }

    // Trigger only on transition processing -> ready
    if (prev === 'processing' && current === 'ready') {
      setShowSuccessFavicon(true);

      successTimeoutRef.current = setTimeout(() => {
        setShowSuccessFavicon(false);
        successTimeoutRef.current = null;
      }, 3000);
    }

    // If we go back to processing or error, hide success immediately
    if (current === 'processing' || current === 'error') {
      setShowSuccessFavicon(false);
    }

    prevSyncStatusRef.current = current;

    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, [effectiveSyncStatus]);

  useEffect(() => {
    if (!id || isDraft || isLoading || userSource || !initialSyncComplete) return;
    console.warn(`[CORTEX_WORKSPACE] Source not found after sync, redirecting to cortex: ${id}`);
    navigateWithSearch(appRoutes.cortex, { replace: true });
  }, [id, isDraft, isLoading, userSource, initialSyncComplete, navigateWithSearch]);

  const attachToOriginNote = async (savedUserSourceId: string) => {
    if (!originNoteLink?.id || hasAttachedOriginNoteRef.current) return;

    hasAttachedOriginNoteRef.current = true;

    try {
      const storedOriginNote = originNote ?? (await queryNote(originNoteLink.id));
      const nextUserSources = {
        ...(storedOriginNote?.toJSON().userSources || {}),
        [savedUserSourceId]: {
          enabled: true,
          index: Date.now(),
        },
      };

      await upsertOriginNote({ userSources: nextUserSources });
    } catch (error) {
      hasAttachedOriginNoteRef.current = false;
      console.error('Failed to attach source to origin note:', error);
    }
  };

  const handleUserSourceUpsert = (payload: Partial<UserSource>, options?: Pick<MutationOptions, 'ifUnmodifiedSince'>) => {
    const payloadWithScope = originNoteLink?.id && !userSource ? { availabilityScope: 'note' as const, ...payload } : payload;

    return upsertUserSource(payloadWithScope, {
      ...options,
      onSuccess: (savedUserSource) => {
        attachToOriginNote(savedUserSource.id);

        const activeRouteDocumentId = getDocumentIdFromPathname(window.location.pathname);
        if (activeRouteDocumentId === savedUserSource.id) {
          navigateWithSearch(`${appRoutes.cortex}/${savedUserSource.id}`, { replace: true });
        }
      },
    });
  };

  const menu = useMemo(() => {
    if (!userSource) {
      return [];
    }
    return getMenuData({ type: 'userSources', ...userSource.toJSON() });
  }, [getMenuData, userSource]);

  const showSuggestions = appReady && !isDraft && !!review?.suggestions.length && editor && review;

  return (
    <Workspace>
      {((!isLoading && !!userSource) || isDraft) && (
        <EditorWorkspace<UserSource>
          id={id}
          editorType="cortex"
          title={documentTitle}
          document={userSource}
          draftId={isDraft ? id : undefined}
          processing={showProcessing}
          data-testid="cortex-workspace"
          isDocumentLoading={isLoading}
          autofocus={true}
          onTitleChange={setDocumentTitle}
          onArchived={() => navigateWithSearch(appRoutes.cortex)}
          upsertDocument={handleUserSourceUpsert}
          processingView={<CortexProcessing userSource={userSource} />}
          header={
            <DocumentHeader
              menu={menu}
              name={documentTitle}
              document={userSource}
              processing={isResyncing}
              onChange={setDocumentTitle}
              documentExists={!!userSource}
              upsertDocument={handleUserSourceUpsert}
              lastSyncTime={userSource?.lastSyncAt}
              lastEditTime={userSource?.updatedAt}
              error={processingFailed ? getUserSourceErrorMessage(userSource?.error, 'short') ?? 'Failed to Process' : undefined}
              bottom={<SourceInput userSource={userSource} />}
              leftActions={editor && <CopyToClipboardBtn editor={editor} />}
            />
          }
        />
      )}
      {showSuggestions &&
        createPortal(
          <SuggestionsNavigation editor={editor} review={review} data-testid="cortex-prompt-box" />,
          document.querySelector('main') ?? document.body
        )}
    </Workspace>
  );
};

const getFaviconStatus = ({
  isResyncing,
  processingFailed,
  showSuccessFavicon,
}: {
  isResyncing: boolean;
  processingFailed: boolean;
  showSuccessFavicon: boolean;
}) => {
  if (isResyncing) {
    return 'loading';
  }

  if (processingFailed) {
    return 'error';
  }

  if (showSuccessFavicon) {
    return 'success';
  }

  return 'idle';
};
